import { z } from "zod";
import { IconSparkles, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  useSearchKeyFactsQuery,
  type KeyFact,
} from "../hooks/useSearchKeyFactsQuery";
import { createSessionStore } from "../../../lib/store";

// Dismissal is per-session AND per-query: dismissing the card for
// "myasthenia gravis" must not silently hide it when the user types a new
// query. We key the store on the trimmed search value.
const dismissedQueriesStore = createSessionStore<readonly string[]>(
  "search-key-facts-dismissed",
  [],
  z.array(z.string())
);

export function SearchKeyFacts({
  searchValue,
  articleIds,
}: {
  searchValue: string;
  articleIds: readonly string[];
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = dismissedQueriesStore();
  const { facts, isLoading } = useSearchKeyFactsQuery(searchValue, articleIds);

  const trimmed = searchValue.trim();
  if (dismissed.includes(trimmed)) return null;
  if (isLoading) return <KeyFactsSkeleton />;
  if (facts.length === 0) return null;

  const prefilledQuestion = [
    t("Tell me more about those statements:"),
    ...facts.map((fact) => `- "${fact.statement}"`),
  ].join("\n");

  return (
    <div className="flex w-full animate-fade-in flex-col gap-1.5 rounded-2xl bg-ds-mint-50/70 p-3.5">
      <Header
        onDismiss={() => {
          setDismissed((prev) =>
            prev.includes(trimmed) ? prev : [...prev, trimmed]
          );
        }}
      />
      <ul className="flex w-full flex-col divide-y divide-ds-mint-200">
        {facts.map((fact, i) => (
          <FactRow
            key={`${String(i)}-${fact.statement.slice(0, 16)}`}
            fact={fact}
            index={i}
          />
        ))}
      </ul>
      <Link
        to={{
          pathname: "/gpt/chat/new",
          state: { initialQuestion: prefilledQuestion },
        }}
        className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-white/30 bg-ds-lime-400 px-4 py-2 text-sm font-medium tracking-tight text-ds-mint-800"
      >
        <IconSparkles size={16} />
        {t("Ask JuisciGPT")}
      </Link>
    </div>
  );
}

function Header({ onDismiss }: { onDismiss: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex w-full items-center gap-1">
      <IconSparkles size={16} className="text-ds-mint-700" />
      <p className="flex-1 text-xs font-medium text-ds-mint-700">
        {t("KEY FACTS")}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t("Dismiss key facts")}
        className="flex size-4 items-center justify-center text-ds-slate-400"
      >
        <IconX size={16} />
      </button>
    </div>
  );
}

function FactRow({
  fact,
  index,
}: {
  fact: KeyFact;
  index: number;
}): React.JSX.Element {
  return (
    <li
      className="flex animate-fade-in-up items-start gap-2 py-2.5 [animation-fill-mode:backwards]"
      style={{ animationDelay: `${String(200 + index * 120)}ms` }}
    >
      <span
        aria-hidden
        className="mt-1.5 size-1.5 shrink-0 rounded-full bg-ds-mint-700"
      />
      <span className="text-sm/snug  text-ds-black">{fact.statement}</span>
    </li>
  );
}

function KeyFactsSkeleton(): React.JSX.Element {
  return (
    <div className="flex w-full flex-col gap-2 rounded-2xl bg-ds-mint-50/70 p-3.5">
      <div className="h-4 w-24 animate-pulse rounded bg-ds-mint-100" />
      <div className="h-12 w-full animate-pulse rounded bg-ds-mint-100" />
      <div className="h-12 w-full animate-pulse rounded bg-ds-mint-100" />
    </div>
  );
}
